import { emitUnhandledError, logger } from '@/ui'
import { FileIcon, ImageIcon, LayoutTemplateIcon, Loader2Icon, PaperclipIcon, SendIcon, XIcon } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ConversationWindow } from './types'

const MAX_FILE_SIZE = 16 * 1024 * 1024 // 16MB - WhatsApp limit
const ALLOWED_MEDIA_TYPES = [
	'image/jpeg',
	'image/png',
	'image/webp',
	'application/pdf',
	'video/mp4',
	'video/3gpp',
	'audio/aac',
	'audio/mp4',
	'audio/mpeg',
	'audio/ogg',
]

export interface WhatsAppComposerBarProps {
	conversationWindow: ConversationWindow | null
	onSendText: (content: string, mediaFile?: File) => Promise<void>
	onOpenTemplates: () => void
}

export function WhatsAppComposerBar({ conversationWindow, onSendText, onOpenTemplates }: WhatsAppComposerBarProps) {
	const { t } = useTranslation('common')
	const windowOpen = conversationWindow?.isOpen ?? false

	const [textContent, setTextContent] = useState('')
	const [mediaFile, setMediaFile] = useState<File | null>(null)
	const [mediaPreview, setMediaPreview] = useState<string | null>(null)
	const [isSending, setIsSending] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) {
			return
		}

		if (!ALLOWED_MEDIA_TYPES.includes(file.type)) {
			emitUnhandledError({
				title: t('whatsapp.errorFileType'),
				description: t('whatsapp.errorFileTypeDesc'),
			})
			return
		}

		if (file.size > MAX_FILE_SIZE) {
			emitUnhandledError({
				title: t('whatsapp.errorFileSize'),
				description: t('whatsapp.errorFileSizeDesc'),
			})
			return
		}

		setMediaFile(file)

		if (file.type.startsWith('image/')) {
			const reader = new FileReader()
			reader.onload = (event) => setMediaPreview(event.target?.result as string)
			reader.readAsDataURL(file)
		} else {
			setMediaPreview(null)
		}
	}, [t])

	const handleRemoveMedia = useCallback(() => {
		setMediaFile(null)
		setMediaPreview(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}, [])

	const handleSend = useCallback(async () => {
		if (!textContent.trim()) {
			if (mediaFile) {
				emitUnhandledError({
					title: t('whatsapp.errorMessageRequired'),
					description: t('whatsapp.errorMessageRequiredDesc'),
				})
			}
			return
		}

		setIsSending(true)
		try {
			await onSendText(textContent.trim(), mediaFile ?? undefined)
			setTextContent('')
			setMediaFile(null)
			setMediaPreview(null)
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}
		} catch (err) {
			logger.error(err, {
				tags: { component: 'WhatsAppComposerBar', action: 'sendText' },
			})
			emitUnhandledError({
				title: t('whatsapp.errorSendTitle'),
				description: err instanceof Error ? err.message : t('whatsapp.errorDefault'),
				cause: err,
			})
		} finally {
			setIsSending(false)
		}
	}, [textContent, mediaFile, onSendText, t])

	const canSend = textContent.trim().length > 0 && !isSending

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			if (canSend) {
				void handleSend()
			}
		}
	}

	// Outside the 24h window, WhatsApp only allows pre-approved templates — lock the
	// free-text input and point the user straight at the template sheet.
	if (!windowOpen) {
		return (
			<div className="border-t border-grey-100 bg-white px-3 py-3">
				<button
					type="button"
					onClick={onOpenTemplates}
					className="flex w-full items-center justify-center gap-2 rounded-2xl border border-grey-200 bg-grey-50 px-4 py-3 text-sm font-medium text-grey-700 transition-colors hover:bg-grey-100"
				>
					<LayoutTemplateIcon className="size-4 text-brand-600" />
					{t('whatsapp.startWithTemplate')}
				</button>
				<p className="mt-1.5 text-center text-xs text-grey-400">{t('whatsapp.windowClosedHint')}</p>
			</div>
		)
	}

	return (
		<div className="border-t border-grey-100 bg-white px-3 py-2.5">
			{mediaFile && (
				<div className="mb-2 flex items-center gap-3 rounded-lg border border-grey-200 bg-grey-50 p-2">
					{mediaPreview
						? (
							<img
								src={mediaPreview}
								alt={t('whatsapp.messagePreview')}
								className="size-10 rounded object-cover"
							/>
						)
						: (
							<div className="flex size-10 items-center justify-center rounded bg-grey-200">
								{mediaFile.type === 'application/pdf'
									? <FileIcon className="size-5 text-grey-500" />
									: <ImageIcon className="size-5 text-grey-500" />}
							</div>
						)}
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-medium text-grey-900">{mediaFile.name}</p>
						<p className="text-xs text-grey-500">{(mediaFile.size / 1024 / 1024).toFixed(2)} MB</p>
					</div>
					<button
						type="button"
						onClick={handleRemoveMedia}
						aria-label={t('common.dismiss')}
						className="p-1 text-grey-400 hover:text-grey-600"
					>
						<XIcon className="size-4" />
					</button>
				</div>
			)}

			<div className="flex items-end gap-1">
				<input
					ref={fileInputRef}
					type="file"
					accept={ALLOWED_MEDIA_TYPES.join(',')}
					onChange={handleFileSelect}
					className="hidden"
				/>
				<button
					type="button"
					onClick={() => fileInputRef.current?.click()}
					aria-label={t('whatsapp.addMedia')}
					className="flex-shrink-0 p-2 text-grey-400 transition-colors hover:text-grey-600"
				>
					<PaperclipIcon className="size-5" />
				</button>
				<button
					type="button"
					onClick={onOpenTemplates}
					aria-label={t('whatsapp.useTemplate')}
					className="flex-shrink-0 p-2 text-grey-400 transition-colors hover:text-brand-600"
				>
					<LayoutTemplateIcon className="size-5" />
				</button>
				<textarea
					value={textContent}
					onChange={(e) => setTextContent(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={t('whatsapp.messagePlaceholder')}
					maxLength={4096}
					rows={1}
					className="max-h-32 flex-1 resize-none overflow-y-auto rounded-2xl bg-grey-50 px-4 py-2.5 text-sm text-grey-900 placeholder:text-grey-400 field-sizing-content focus:outline-none focus:ring-2 focus:ring-brand-600/20"
				/>
				<button
					type="button"
					onClick={() => void handleSend()}
					disabled={!canSend}
					aria-label={t('common.send')}
					className="flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-white transition-colors hover:bg-brand-700 disabled:bg-grey-200 disabled:text-grey-400"
				>
					{isSending ? <Loader2Icon className="size-4 animate-spin" /> : <SendIcon className="size-4" />}
				</button>
			</div>
		</div>
	)
}
